import React, { useState, useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useUser } from "../../authentification/UserContext";
import { API_ENDPOINTS } from "../../../../ApiService";
import apiService from "../../../../ApiService";

import {
  Box,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  Paper,
} from "@mui/material";
import styled from "styled-components";
import { commonColors } from "../../../../theme/colors";
import Grid from "@mui/material/Unstable_Grid2";
import { tokens } from "../../../../theme";
import StatBox from "../../../components/statBox/StatBox";
import Nutrition from "../../../models/Nutrition";
import WeightDifferenceCard from "../../../components/clientDashboard/WeightDifferenceCard";
import Title from "../../../components/title/Title";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import BakeryDiningIcon from "@mui/icons-material/BakeryDining";
import PieChart from "../../../components/charts/pieChart/PieChart";
import ScaleIcon from "@mui/icons-material/Scale";
import SetMealIcon from "@mui/icons-material/SetMeal";
import LocalPizzaIcon from "@mui/icons-material/LocalPizza";
import { MainContainer } from "../../styles/sharedStyles";
import ApexBarChart from "../../../components/charts/barChart/ApexBarChart";
import HorizontalStackedBarChart from "../../../components/charts/barChart/VerticalStackedBarChart";
import ApexPieChart from "../../../components/charts/pieChart/ApexPieChart";
import VerticalStackedBarChart from "../../../components/charts/barChart/VerticalStackedBarChart";
import WeightGraph from "../../../components/clientDashboard/weight/WeightGraph";
import MensurationGraph from "../../../components/clientDashboard/mensuration/MensurationGraph";

// Reusable DashboardCard component with fallback for theme and full width
const DashboardCard = styled(Paper)`
  width: 100%;
  padding: 1.5rem;
  border-radius: 10px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  background-color: ${({ theme }) =>
    theme &&
    theme.palette &&
    theme.palette.primary &&
    theme.palette.primary.main
      ? theme.palette.primary.main
      : "#1976D2"};
`;

const ClientDashboard = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.up("sm"));
  const colors = tokens(theme.palette.mode);

  // Retrieve clientID from route parameters
  const { clientID } = useParams();
  const [clientData, setClientData] = useState(null);
  const [poids, setPoids] = useState([]);
  const [nutritionData, setNutritionData] = useState([]);
  const navigate = useNavigate();
  const { user } = useUser();

  // Clear data when clientID changes
  useEffect(() => {
    setClientData(null);
    setPoids([]);
    setNutritionData([]);
  }, [clientID]);

  // Fetch client dashboard data
  useEffect(() => {
    const fetchClientData = async () => {
      try {
        const response = await apiService.get(
          `${API_ENDPOINTS.CLIENTDASHBOARD}/${clientID}`
        );
        setClientData(response.data);
      } catch (error) {
        console.error("Error fetching client data:", error);
      }
    };

    if (clientID) {
      fetchClientData();
    }
  }, [clientID]);

  // Fetch weight data
  useEffect(() => {
    const fetchPoids = async () => {
      try {
        if (clientID) {
          const response = await apiService.get(
            API_ENDPOINTS.CLIENT_POIDS(clientID)
          );
          const poidsData = response.data;
          // Sort by date descending
          poidsData.sort(
            (a, b) => new Date(b.datepoids) - new Date(a.datepoids)
          );
          setPoids(poidsData);
        }
      } catch (error) {
        console.error("Error fetching poids:", error);
      }
    };

    fetchPoids();
  }, [clientID]);

  // Fetch nutrition data
  useEffect(() => {
    const fetchNutrition = async () => {
      try {
        if (clientID) {
          const response = await apiService.get(
            API_ENDPOINTS.CLIENT_NUTRITION(clientID)
          );
          const nutrition = response.data.map((data) => new Nutrition(data));
          setNutritionData(nutrition);
        }
      } catch (error) {
        console.error("Error fetching nutrition:", error);
      }
    };

    fetchNutrition();
  }, [clientID]);

  // Prepare chart data for weight measurements
  const weightMensurementsChartData = [
    {
      id: "Poids",
      color: "hsl(134, 70%, 50%)",
      data: poids
        .map((item) => ({
          x: new Date(item.datepoids).getTime(),
          y: item.poids,
        }))
        .sort((a, b) => a.x - b.x),
    },
  ];

  // Format timestamp to "Month Day"
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const options = { month: "short", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  weightMensurementsChartData.forEach((dataObj) => {
    dataObj.data = dataObj.data.map((item) => ({
      x: formatTimestamp(item.x),
      y: item.y,
    }));
  });

  // Format nutrition data for PieChart
  const formattedNutritionData = nutritionData.flatMap((nutri, index) => [
    {
      id: "Protein",
      label: "Protéines",
      value: nutri.proteines,
    },
    {
      id: "Carbs",
      label: "Glucides",
      value: nutri.glucides,
    },
    {
      id: "Fat",
      label: "Lipides",
      value: nutri.lipides,
    },
  ]);

  // Utility functions for macros calculations
  const calculateProteines = (weight, multiplier) =>
    (weight * multiplier).toFixed(0);
  const calculateLipides = (apportcalorique, multiplier) =>
    (((apportcalorique * 0.4) / 9) * multiplier).toFixed(0);
  const calculateGlucides = (apportcalorique, proteines, multiplier) => {
    const remainingCalories =
      apportcalorique - apportcalorique * 0.4 - proteines * 4;
    return ((remainingCalories / 4) * multiplier).toFixed(0);
  };

  // Format nutrition data per meal for BarChart
  const formattedNutritionDataPerMeal = [
    {
      meal: "Breakfast",
      protein: calculateProteines(clientData?.poidsactuel, 0.4),
      fat: calculateLipides(nutritionData[0]?.apportcalorique, 0.25),
      carbs: calculateGlucides(
        nutritionData[0]?.apportcalorique,
        nutritionData[0]?.proteines,
        0.2
      ),
    },
    {
      meal: "Lunch",
      protein: calculateProteines(clientData?.poidsactuel, 0.3),
      fat: calculateLipides(nutritionData[0]?.apportcalorique, 0.25),
      carbs: calculateGlucides(
        nutritionData[0]?.apportcalorique,
        nutritionData[0]?.proteines,
        0.15
      ),
    },
    {
      meal: "Snack",
      protein: calculateProteines(clientData?.poidsactuel, 0.3),
      fat: calculateLipides(nutritionData[0]?.apportcalorique, 0.1),
      carbs: calculateGlucides(
        nutritionData[0]?.apportcalorique,
        nutritionData[0]?.proteines,
        0.2
      ),
    },
    {
      meal: "Dinner",
      protein: calculateProteines(clientData?.poidsactuel, 0.6),
      fat: calculateLipides(nutritionData[0]?.apportcalorique, 0.4),
      carbs: calculateGlucides(
        nutritionData[0]?.apportcalorique,
        nutritionData[0]?.proteines,
        0.5
      ),
    },
  ];

  if (!clientData) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Calculate weight difference and related info
  const weightDifference =
    poids && poids.length > 0
      ? (poids[0].poids - (clientData?.poidsdorigine ?? 0)).toFixed(1)
      : 0;
  const subtitle = weightDifference >= 0 ? "Weight gain" : "Weight loss";
  const isDataLoaded = poids && poids.length > 0;

  const handleDownloadReports = async () => {
    try {
      const response = await apiService.get(
        API_ENDPOINTS.DOWNLOAD_REPORTS(clientID),
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "dashboard_report.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error("Error downloading report:", error);
    }
  };

  return (
    <MainContainer>
      <Box
        sx={{ p: { xs: "1rem", sm: "2rem", md: "3rem" }, overflowX: "auto" }}
      >
        <Title
          title="Tableau de bord"
          summary="Une vue d'ensemble de tes informations"
        />
        <Box sx={{ mt: 4 }}>
          {/* Header with Download Button */}
          <Box
            display={smScreen ? "flex" : "block"}
            flexDirection={smScreen ? "row" : "column"}
            justifyContent={smScreen ? "space-between" : "start"}
            alignItems={smScreen ? "center" : "start"}
            m="10px 0"
          >
            <Box>
              <Button
                onClick={handleDownloadReports}
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: colors.grey[100],
                  fontSize: "14px",
                  fontWeight: "bold",
                  padding: "10px 20px",
                }}
              >
                <DownloadOutlinedIcon sx={{ mr: "10px" }} />
                Download Reports
              </Button>
            </Box>
          </Box>

          {/* Responsive Grid for Stats and Charts */}
          <Grid container spacing={3}>
            {/* Weight Stat or "No data" prompt */}
            <Grid xs={12} sm={12} md={6} lg={3} xl={3}>
              <DashboardCard>
                {isDataLoaded ? (
                  <StatBox
                    title={
                      <WeightDifferenceCard
                        weightDifference={weightDifference}
                      />
                    }
                    subtitle={
                      weightDifference >= 0 ? "Weight gain" : "Weight loss"
                    }
                    progress="0.75"
                    increase="+14%"
                    icon={
                      <ScaleIcon sx={{ color: "#1976D2", fontSize: "40px" }} />
                    }
                  />
                ) : (
                  <Box textAlign="center">
                    <Typography variant="h6">
                      No weight data available.
                    </Typography>
                    <Button
                      onClick={() => navigate(`/weightupdate/${clientID}`)}
                      sx={{ mt: 2 }}
                    >
                      Add Weight
                    </Button>
                  </Box>
                )}
              </DashboardCard>
            </Grid>

            <Grid xs={12} sm={12} md={6} lg={3} xl={3}>
              <DashboardCard>
                {nutritionData.length > 0 ? (
                  <StatBox
                    title={`${nutritionData[0].apportcalorique} Kcal`}
                    subtitle="Calories per day"
                    progress="0.50"
                    increase="+21%"
                    icon={
                      <RestaurantIcon
                        sx={{ color: "#1976D2", fontSize: "40px" }}
                      />
                    }
                  />
                ) : (
                  <Typography>Loading...</Typography>
                )}
              </DashboardCard>
            </Grid>

            <Grid xs={12} sm={12} md={6} lg={2} xl={2}>
              <DashboardCard>
                {nutritionData.length > 0 ? (
                  <StatBox
                    title={`${nutritionData[0].proteines} g`}
                    subtitle="Proteins per day"
                    progress="0.30"
                    increase="+5%"
                    color={commonColors.protein}
                    icon={
                      <SetMealIcon
                        sx={{ color: commonColors.protein, fontSize: "40px" }}
                      />
                    }
                  />
                ) : (
                  <Typography>Loading...</Typography>
                )}
              </DashboardCard>
            </Grid>

            <Grid xs={12} sm={12} md={6} lg={2} xl={2}>
              <DashboardCard>
                {nutritionData.length > 0 ? (
                  <StatBox
                    title={`${nutritionData[0].lipides} g`}
                    subtitle="Fat per day"
                    progress="0.80"
                    increase="+43%"
                    color={commonColors.fat}
                    icon={
                      <BakeryDiningIcon
                        sx={{ color: commonColors.fat, fontSize: "40px" }}
                      />
                    }
                  />
                ) : (
                  <Typography>Loading...</Typography>
                )}
              </DashboardCard>
            </Grid>

            <Grid xs={12} sm={12} md={6} lg={2} xl={2}>
              <DashboardCard>
                {nutritionData.length > 0 ? (
                  <StatBox
                    title={`${nutritionData[0].glucides} g`}
                    subtitle="Carbs per day"
                    progress="0.80"
                    increase="+43%"
                    color={commonColors.carbs}
                    icon={
                      <LocalPizzaIcon
                        sx={{ color: commonColors.carbs, fontSize: "40px" }}
                      />
                    }
                  />
                ) : (
                  <Typography>Loading...</Typography>
                )}
              </DashboardCard>
            </Grid>

            {nutritionData.length > 0 && (
              <>
                <Grid xs={12} sm={12} md={6} lg={6} xl={6}>
                  <DashboardCard>
                    <Typography variant="h5" fontWeight="600" mb="1rem">
                      Macros par jour (g)
                    </Typography>
                    <Box height="350px">
                      <ApexPieChart
                        dataNutrition={formattedNutritionData}
                        isDashboard={true}
                      />
                    </Box>
                  </DashboardCard>
                </Grid>
                <Grid xs={12} sm={12} md={6} lg={6} xl={6}>
                  <DashboardCard>
                    <Typography variant="h5" fontWeight="600" mb="1rem">
                      Macros par repas par jour (g)
                    </Typography>
                    <Box height="350px">
                      <VerticalStackedBarChart
                        dataNutritionPerMeal={formattedNutritionDataPerMeal}
                        isDashboard={true}
                      />
                    </Box>
                  </DashboardCard>
                </Grid>
              </>
            )}
          </Grid>
          <Box>
            <Grid container spacing={3}>
              {/* Weight Card */}
              <Grid item xs={12} md={6}>
                <DashboardCard>
                  <Typography
                    variant="h5"
                    fontWeight="600"
                    mb="1rem"
                    align="center"
                  >
                    Courbe du poids
                  </Typography>
                  <WeightGraph />
                </DashboardCard>
              </Grid>
              {/* Mensurations Card */}
              <Grid item xs={12} md={6}>
                <DashboardCard>
                  <Typography
                    variant="h5"
                    fontWeight="600"
                    mb="1rem"
                    align="center"
                  >
                    Courbes des mensurations
                  </Typography>
                  <MensurationGraph />
                </DashboardCard>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </MainContainer>
  );
};

export default ClientDashboard;
